import { useEffect, useRef } from 'react';

// Build-version watchdog.
//
// `updateBuildJSON.js` writes the current build's timestamp into public/meta.json
// at build time. This hook compares that timestamp against the one the browser
// last saw and force-reloads the tab when a newer build has been deployed, so
// nobody keeps running stale JS/CSS after a release.

const BUILD_KEY = 'buildDateTime';
const RELOAD_GUARD_KEY = 'buildReloadedAt';

// How often to re-check while the tab stays open. The admin is a SPA that people
// leave open for hours, so a mount-only check would miss most deploys.
const POLL_INTERVAL = 5 * 60 * 1000;

// If we reloaded this recently, don't reload again. Protects against a reload
// loop if meta.json ever reports an inconsistent build (e.g. two servers behind
// a load balancer running different releases).
const RELOAD_COOLDOWN = 60 * 1000;

const fetchLatestBuildTime = async () => {
  // meta.json must never be served from the browser/CDN cache — a cached copy
  // reports the old build forever and the check silently stops working.
  const res = await fetch(`/meta.json?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      Pragma: 'no-cache',
      Expires: '-1',
      'Cache-Control': 'no-cache',
    },
  });
  if (!res.ok) {
    throw new Error(`meta.json responded with ${res.status}`);
  }
  const meta = await res.json();
  const buildTime = Number(meta.buildDateTime);
  return Number.isFinite(buildTime) && buildTime > 0 ? buildTime : null;
};

const hardReload = async () => {
  // Drop anything a previous service worker left behind, otherwise the reload
  // can be answered from the cache with the very build we're trying to replace.
  if (window.caches) {
    try {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    } catch (error) {
      // Cache API unavailable (private mode, older browser) — reload anyway.
    }
  }

  try {
    // Revalidates index.html in the HTTP cache so the reload below picks up the
    // new document, and with it the new hashed bundle filenames.
    await fetch(window.location.href, { cache: 'reload', credentials: 'same-origin' });
  } catch (error) {
    // Network blip — the reload will hit the network on its own.
  }

  window.location.reload();
};

const useBuildVersionCheck = () => {
  const running = useRef(false);

  useEffect(() => {
    const check = async () => {
      if (running.current || document.hidden) return;
      running.current = true;

      try {
        const latest = await fetchLatestBuildTime();
        if (latest === null) return;

        const seen = Number(localStorage.getItem(BUILD_KEY));

        // First load in this browser: record the build, don't reload — there is
        // nothing stale to replace yet.
        if (!seen) {
          localStorage.setItem(BUILD_KEY, String(latest));
          return;
        }

        if (latest <= seen) return;

        const reloadedAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
        if (reloadedAt && Date.now() - reloadedAt < RELOAD_COOLDOWN) return;

        localStorage.setItem(BUILD_KEY, String(latest));
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
        await hardReload();
      } catch (error) {
        // Offline, or meta.json is temporarily unreachable. Leave the stored
        // build time alone and try again on the next tick.
      } finally {
        running.current = false;
      }
    };

    check();

    const interval = setInterval(check, POLL_INTERVAL);
    const onFocus = () => check();

    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
};

export default useBuildVersionCheck;
export { useBuildVersionCheck };
